import React, { useEffect, useMemo, useRef, useState } from 'react'
import { IoIosArrowRoundBack } from 'react-icons/io'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { FiUploadCloud } from 'react-icons/fi'
import { useLocations } from '../../hooks/useLocations'
import api, { fetcher } from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import useSWR from 'swr'
import { FaRegCheckCircle } from 'react-icons/fa'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { getMainCategories } from '../../utils/productCategories'
import { trimFormValue } from '../../utils/formValues'

const trimField = { setValueAs: trimFormValue }

const MAX_IMAGES = 5
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png']

const AddStock = () => {
  const { regionNames } = useLocations()
  const history = useHistory()
  const [images, setImages] = useState(Array(MAX_IMAGES).fill(null))
  const [imageFiles, setImageFiles] = useState(Array(MAX_IMAGES).fill(null))
  const fileInputs = useRef([])
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme')
    if (stored) return stored === 'dark'
    localStorage.setItem('theme', 'dark')
    return true
  })

  // Fetch all required data
  const { data: categories } = useSWR(`service-orders/products/categories`, fetcher)
  const { data: animalTypes } = useSWR(`service-orders/products/animal_types/`, fetcher)
  const { data: productTypes } = useSWR(`service-orders/products/product_types/`, fetcher)

  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      category_id: '',
      product_name: '',
      country: '',
      region: '',
      community: '',
      warranty_offered: 'Yes',
      quantity: '',
      description: '',
      unit_price: '',
      is_negotiable: false,
      allow_bulk_price: 'Yes',
      delivery_countries: [],
      delivery_regions: [],
      delivery_communities: [],
      animal_type_id: '',
      visible: true,
    }
  })

  const watchedValues = watch()

  const mainCategories = useMemo(
    () => getMainCategories(categories),
    [categories]
  )

  // // console.log("watchedValues", categories)
  // console.log("watchedValues.category_id", watchedValues.category_id)
  // console.log("categories", categories)

  const validateImages = () => {
    const hasImages = imageFiles.some(file => file instanceof File)
    if (!hasImages) {
      return 'At least one product image is required'
    }

    const imageCount = imageFiles.filter(file => file instanceof File).length
    if (imageCount < 2) {
      return 'At least 2 product images are required'
    }

    return true
  }

  const onSubmit = async (data) => {
    try {
      // Validate images before submission
      const imageValidation = validateImages()
      if (imageValidation !== true) {
        ErrorNotification(imageValidation)
        return
      }

      const formData = new FormData()

      // Product Details
      formData.append('category_id', data.category_id)
      formData.append('main_category_ids', parseInt(data.category_id))
      formData.append('product_name', data.product_name)
      formData.append('name', data.product_name)
      formData.append('country', data.country)
      formData.append('region', data.region)
      formData.append('community', data.community)
      formData.append('warranty_offered', data.warranty_offered)
      formData.append('animal_type_id', data.animal_type_id)
      formData.append('quantity', data.quantity)
      formData.append('description', data.description)
      formData.append('unit_price', data.unit_price)
      formData.append('is_negotiable', data.is_negotiable)
      formData.append('allow_bulk_price', data.allow_bulk_price)

      // Delivery Locations - Convert to JSON arrays
      formData.append('delivery_countries', JSON.stringify(data.delivery_countries))
      formData.append('delivery_regions', JSON.stringify(data.delivery_regions))
      formData.append('delivery_communities', JSON.stringify(data.delivery_communities))

      // Visibility
      formData.append('is_active', data.visible)

      // Handle image files
      imageFiles.forEach((file, index) => {
        if (file instanceof File) {
          formData.append('uploaded_images', file)
        }
      })

      const response = await api.post('service-orders/products/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const imageResponse = await api.patch(`service-orders/products/${response.data.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.status === 201 && imageResponse?.status === 200) {
        SuccessNotification("Product added successfully")
        history.goBack()
      }
    } catch (error) {
      ErrorNotification(error.response?.data?.message || "An error occurred")
    }
  }

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only JPG, JPEG, and PNG files are allowed'
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 5MB'
    }
    return null
  }

  const handleImageChange = (idx, e) => {
    const file = e.target.files[0]
    if (file) {
      const validationError = validateFile(file)
      if (validationError) {
        ErrorNotification(validationError)
        return
      }

      const reader = new FileReader()
      reader.onloadend = () => {
        // Update preview images
        setImages(prev => {
          const newImages = [...prev]
          newImages[idx] = reader.result
          return newImages
        })

        // Update file objects
        setImageFiles(prev => {
          const newFiles = [...prev]
          newFiles[idx] = file
          return newFiles
        })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = (idx) => {
    setImages(prev => {
      const newImages = [...prev]
      newImages[idx] = null
      return newImages
    })

    setImageFiles(prev => {
      const newFiles = [...prev]
      newFiles[idx] = null
      return newFiles
    })

    if (fileInputs.current[idx]) {
      fileInputs.current[idx].value = ''
    }
  }

  // React Select custom styles
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: state.isFocused ? '#fbbf24' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(251, 191, 36, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#fbbf24'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#fbbf24' : state.isFocused ? '#fef3c7' : 'white',
      color: state.isSelected ? 'white' : 'black',
      '&:hover': {
        backgroundColor: state.isSelected ? '#fbbf24' : '#fef3c7'
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#fef3c7'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#92400e'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#92400e',
      '&:hover': {
        backgroundColor: '#fbbf24',
        color: 'white'
      }
    })
  }

  // Dark mode styles
  const darkSelectStyles = {
    container: (provided) => ({
      ...provided,
      width: '100%',
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#2e2e2e' : '#fff',
      borderColor: state.isFocused ? '#2e2e2e' : '#2e2e2e',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(251, 191, 36, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#fbbf24'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#1f2937'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#fbbf24' : state.isFocused ? '#374151' : '#1f2937',
      color: state.isSelected ? 'white' : '#d1d5db',
      '&:hover': {
        backgroundColor: state.isSelected ? '#fbbf24' : '#374151'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#d1d5db'
    }),
    input: (provided) => ({
      ...provided,
      color: '#d1d5db'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#fef3c7'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#92400e'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#92400e',
      '&:hover': {
        backgroundColor: '#fbbf24',
        color: 'white'
      }
    })
  }

  useEffect(() => {
    const stored = localStorage.getItem('theme')
    if (stored) setIsDarkMode(stored === 'dark')

    // listen for theme change
    const handleThemeChange = () => {
      const stored = localStorage.getItem('theme')
      if (stored) setIsDarkMode(stored === 'dark')
    }
    window.addEventListener('themeChange', handleThemeChange)
    return () => window.removeEventListener('themeChange', handleThemeChange)
  }, [])

  return (
    <div className="w-full lg:px-10 px-2 h-full py-6 lg:py-10">
      <div className="w-full">
        <button onClick={() => history.goBack()} className="mb-2 flex flex-row items-center">
          <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
          <p className="text-sm font-light capitalize dark:text-white">Back</p>
        </button>

        <h2 className="text-2xl font-bold mb-4 dark:text-white">Add New Stock</h2>

        <form className='bg-white dark:bg-dark rounded-lg overflow-hidden' onSubmit={handleSubmit(onSubmit)}>
          <div className="px-3 py-4 md:px-8 md:py-8 overflow-x-hidden">

            {/* Product Details Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Product Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Category<span className="text-red-500">*</span></label>
                  <select
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                    {...register('category_id', { required: 'Category is required' })}
                  >
                    <option value="">Select category</option>
                    {mainCategories.map((opt) => (
                      <option key={opt?.id} value={opt?.id}>{opt?.name}</option>
                    ))}
                  </select>
                  {errors.category_id && <p className="text-red-500 text-xs mt-1">{errors.category_id.message}</p>}
                </div>

                {/* <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Sub Category<span className="text-red-500">*</span></label>
                  <select
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                    {...register('category_id', { required: 'Category is required' })}
                  >
                    <option value="">Select category</option>
                    {categories?.map(opt => <option key={opt?.id} value={opt?.id}>{opt?.name}</option>)}
                  </select>
                  {errors.category_id && <p className="text-red-500 text-xs mt-1">{errors.category_id.message}</p>}
                </div> */}

              </div>
            </div>


            {watchedValues.category_id && (
              <>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Product Name */}
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Product<span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter product name"
                      maxLength={100}
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      {...register('product_name', {
                        ...trimField,
                        required: 'Product name is required',
                        minLength: { value: 3, message: 'Product name must be at least 3 characters long' },
                        maxLength: { value: 100, message: 'Product name cannot exceed 100 characters' }
                      })}
                    />
                    {errors.product_name && <p className="text-red-500 text-xs mt-1">{errors.product_name.message}</p>}
                  </div>

                  {/* product type */}
                  {/* show only for feed and seed */}
                  {categories?.find(cat => cat?.id === Number(watchedValues.category_id))?.name?.toLowerCase()?.includes('feed') || categories?.find(cat => cat?.id === Number(watchedValues.category_id))?.name?.toLowerCase()?.includes('seed') ? (
                    <div>
                      <label className="block text-sm font-medium mb-1 dark:text-white">
                        Sub Category
                        <span className="text-red-500">*</span></label>
                      <select
                        className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                        {...register('product_type', { required: 'Product type is required' })}
                      >
                        <option value="">Select product type</option>
                        {productTypes?.map(opt => <option key={opt?.id} value={opt?.id}>{opt?.name}</option>)}
                      </select>
                      {errors.product_type && <p className="text-red-500 text-xs mt-1">{errors.product_type.message}</p>}
                    </div>
                  ) : null}

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Select Country<span className="text-red-500">*</span></label>
                    <select
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                      {...register('country', { required: 'Country is required' })}
                    >
                      <option value="">Select country</option>
                      {/* {countries?.map(opt => <option key={opt?.name} value={opt?.name}>{opt?.name}</option>)} */}
                      <option key="Ghana" value="Ghana">Ghana</option>
                    </select>
                    {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country.message}</p>}
                  </div>

                  {/* Region/State */}
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Region/State<span className="text-red-500">*</span></label>
                    <select
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                      {...register('region', { required: 'Region/State is required' })}
                    >
                      <option value="">Select region</option>
                      {regionNames.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    {errors.region && <p className="text-red-500 text-xs mt-1">{errors.region.message}</p>}
                  </div>

                  {/* Town/Community */}
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Town/Community<span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Enter your community"
                      maxLength={100}
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      {...register('community', {
                        ...trimField,
                        required: 'Town/Community is required',
                        minLength: { value: 2, message: 'Community name must be at least 2 characters long' },
                        maxLength: { value: 100, message: 'Community name cannot exceed 100 characters' }
                      })}
                    />
                    {errors.community && <p className="text-red-500 text-xs mt-1">{errors.community.message}</p>}
                  </div>


                  {/* animal type */}
                  {categories?.find(cat => cat?.id === Number(watchedValues.category_id))?.name?.toLowerCase()?.includes('animal') ? (
                    <div className="md:col-span-2 w-full">
                      <label className="block text-sm font-medium mb-1 dark:text-white">Animal Type<span className="text-red-500">*</span></label>
                      <div className="flex flex-row gap-4 flex-wrap w-full">
                        {animalTypes?.map((item, idx) => (
                          <button type="button" key={idx} className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-full py-2 px-3 flex flex-row items-center gap-2 text-sm" onClick={() => setValue('animal_type_id', item?.id)}>
                            <p className="text-black dark:text-white">{item?.name}</p>
                            {watchedValues.animal_type_id === item?.id ? <FaRegCheckCircle className="w-4 h-4 text-yellowcolor" /> : <div className="w-4 h-4 border border-gray-500 rounded-full" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}



                  {/* Warranty Offered */}
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Warranty Offered<span className="text-red-500">*</span></label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="No"
                          {...register('warranty_offered', { required: 'Warranty option is required' })}
                          className="mr-2 form-radio text-yellowcolor focus:ring-yellowcolor"
                        />
                        <span className="text-sm dark:text-white">No</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="Yes"
                          {...register('warranty_offered', { required: 'Warranty option is required' })}
                          className="mr-2 form-radio text-yellowcolor focus:ring-yellowcolor"
                        />
                        <span className="text-sm dark:text-white">Yes</span>
                      </label>
                    </div>
                    {errors.warranty_offered && <p className="text-red-500 text-xs mt-1">{errors.warranty_offered.message}</p>}
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Quantity<span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      placeholder="eg. 300"
                      min={1}
                      max={999999}
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      {...register('quantity', {
                        required: 'Quantity is required',
                        min: { value: 1, message: 'Quantity must be at least 1' },
                        max: { value: 999999, message: 'Quantity cannot exceed 999,999' }
                      })}
                    />
                    {errors.quantity && <p className="text-red-500 text-xs mt-1">{errors.quantity.message}</p>}
                  </div>

                  <div></div>

                  {/* Description - Full Width */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1 dark:text-white">Description<span className="text-red-500">*</span></label>
                    <textarea
                      placeholder='Describe this product'
                      cols={30}
                      rows={6}
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      {...register('description', {
                        ...trimField,
                        required: 'Description is required',
                      })}
                    ></textarea>
                    <div className="flex justify-between items-center mt-1">
                      {errors.description && <p className="text-red-500 text-xs">{errors.description.message}</p>}

                    </div>
                  </div>

                  {/* Product Price */}
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Product Price (GHC)<span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      placeholder="eg. 200"
                      min={1}
                      max={999999}
                      step="0.01"
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      {...register('unit_price', {
                        required: 'Product price is required',
                        min: { value: 1, message: 'Price must be at least GHC1' },
                        max: { value: 999999, message: 'Price cannot exceed GHC999,999' }
                      })}
                    />
                    {errors.unit_price && <p className="text-red-500 text-xs mt-1">{errors.unit_price.message}</p>}
                  </div>

                  {/* Open for Negotiation */}
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Open for Negotiation</label>
                    <div className="flex flex-col sm:flex-row sm:items-center items-start gap-2 sm:gap-3">
                      <label className="relative inline-flex items-center cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          {...register('is_negotiable')}
                          className="sr-only peer"
                          aria-label="Toggle price negotiation"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
                      </label>
                      <span className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">This informs buyers that the price is negotiable</span>
                    </div>
                  </div>

                  {/* Allow Bulk Price */}
                  {/* <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Allow Bulk Price<span className="text-red-500">*</span></label>
                    <div className="space-x-4 flex flex-row">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="No"
                          {...register('allow_bulk_price', { required: 'Bulk price option is required' })}
                          className="mr-2 form-radio text-yellowcolor focus:ring-yellowcolor"
                        />
                        <span className="text-sm dark:text-white">No</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="Yes"
                          {...register('allow_bulk_price', { required: 'Bulk price option is required' })}
                          className="mr-2 form-radio text-yellowcolor focus:ring-yellowcolor"
                        />
                        <span className="text-sm dark:text-white">Yes</span>
                      </label>
                    </div>
                    {errors.allow_bulk_price && <p className="text-red-500 text-xs mt-1">{errors.allow_bulk_price.message}</p>}
                  </div> */}

                </div>

                <hr className='my-8 border-light-white dark:border-border-dark' />

                {/* Delivery Locations Section */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Delivery Locations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Delivery Country */}
                    <div className="w-full min-w-0">
                      <label className="block text-sm font-medium mb-1 dark:text-white">Select Countries<span className="text-red-500">*</span></label>
                      <Select
                        isMulti
                          // options={countries?.map(country => ({ value: country.name, label: country.name }))}
                          options={[{ value: 'Ghana', label: 'Ghana' }]}
                        value={watchedValues.delivery_countries?.map(country => ({ value: 'Ghana', label: 'Ghana' }))}
                        onChange={(selectedOptions) => {
                          const values = selectedOptions ? selectedOptions.map(option => option.value) : []
                          setValue('delivery_countries', values)
                        }}
                        styles={darkSelectStyles}
                        placeholder="Select countries..."
                        className="dark:text-white"
                      />
                      {errors.delivery_countries && <p className="text-red-500 text-xs mt-1">{errors.delivery_countries.message}</p>}
                    </div>

                    {/* Delivery Region */}
                    <div className="w-full min-w-0">
                      <label className="block text-sm font-medium mb-1 dark:text-white">Regions/States<span className="text-red-500">*</span></label>
                      <Select
                        isMulti
                        options={regionNames.map(region => ({ value: region, label: region }))}
                        value={watchedValues.delivery_regions?.map(region => ({ value: region, label: region }))}
                        onChange={(selectedOptions) => {
                          const values = selectedOptions ? selectedOptions.map(option => option.value) : []
                          setValue('delivery_regions', values)
                        }}
                        styles={darkSelectStyles}
                        placeholder="Select regions..."
                        className="dark:text-white"
                      />
                      {errors.delivery_regions && <p className="text-red-500 text-xs mt-1">{errors.delivery_regions.message}</p>}
                    </div>

                    {/* Delivery Community */}
                    <div className="w-full min-w-0 md:col-span-2">
                      <label className="block text-sm font-medium mb-1 dark:text-white">Towns/Communities<span className="text-red-500">*</span></label>
                      <CreatableSelect
                        isMulti
                        options={[]}
                        value={watchedValues.delivery_communities?.map(community => ({ value: community, label: community }))}
                        onChange={(selectedOptions) => {
                          const values = selectedOptions ? selectedOptions.map(option => option.value) : []
                          setValue('delivery_communities', values)
                        }}
                        styles={darkSelectStyles}
                        placeholder="Type and press Enter to add communities..."
                        className="dark:text-white"
                        noOptionsMessage={() => "Type to add a community"}
                        formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                        isValidNewOption={(inputValue) => inputValue.length >= 2}
                        createOptionPosition="first"
                      />
                      {errors.delivery_communities && <p className="text-red-500 text-xs mt-1">{errors.delivery_communities.message}</p>}
                    </div>
                  </div>
                </div>

                <hr className='my-8 border-light-white dark:border-border-dark' />


                {/* Product Images Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Product Images</h3>
                  {/* <div className="mb-4">
                    <label className="block text-sm font-medium mb-2 dark:text-white">Upload images<span className="text-red-500">*</span></label>
                    <div className="border border-dashed border-gray-200 rounded-md p-4 flex flex-col items-center justify-center gap-4 dark:bg-dark dark:border-border-dark">
                      <div className='flex flex-col items-center justify-center bg-gray-50 dark:bg-secondaryDark py-3 px-20 rounded-lg hover:border-yellowcolor transition-colors'>
                        <FiUploadCloud size={48} className="mx-auto text-gray-400 mb-4" />
                        <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Upload 2-5 images</p>
                        <p className="text-sm text-gray-500 mb-4">JPG/PNG max 5MB</p>
                        <button
                          type="button"
                          onClick={() => fileInputs.current[0]?.click()}
                          className="text-yellowcolor hover:text-yellow-600 font-medium cursor-pointer text-sm"
                        >
                          Click to upload
                        </button>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={el => fileInputs.current[0] = el}
                          onChange={e => handleImageChange(0, e)}
                        />
                      </div>
                    </div>
                  </div> */}

                  {/* Image Grid */}
                  {images?.length > 0 && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {images.map((img, idx) => (
                      <div key={idx} className="relative">
                        {img ? (
                          <div className="w-full aspect-square border border-gray-200 dark:border-border-dark rounded-lg overflow-hidden">
                            <img src={img} alt="preview" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(idx)}
                              className="absolute top-1 right-1 bg-white rounded-full w-6 h-6 flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className="w-full aspect-square border border-dashed border-gray-200 dark:border-border-dark rounded-lg flex items-center justify-center bg-gray-50 dark:bg-secondaryDark">
                            <button
                              type="button"
                              onClick={() => fileInputs.current[idx]?.click()}
                              className="text-gray-400 hover:text-yellowcolor transition-colors"
                            >
                              <FiUploadCloud size={24} />
                            </button>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              ref={el => fileInputs.current[idx] = el}
                              onChange={e => handleImageChange(idx, e)}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>}
                </div>

                {/* Visibility Toggle */}
                <div className='flex flex-col items-start sm:items-end justify-end w-full mb-6'>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium mb-1 dark:text-white">Visibility</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        {...register('visible')}
                        className="sr-only peer"
                        aria-label="Toggle product visibility"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
                    </label>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">Toggling visibility on makes your product visible on marketplace</div>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row border-t border-gray-200 dark:border-border-dark p-4 gap-3 sm:justify-end">
            <button
              type="button"
              onClick={() => history.goBack()}
              className="w-full sm:w-auto px-6 text-sm py-2.5 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`w-full sm:w-auto px-6 text-sm py-2.5 ${isSubmitting ? 'bg-gray-200' : 'bg-yellowcolor'} text-black rounded-lg font-semibold hover:bg-yellow-400 flex items-center justify-center gap-2`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : "Add New Stock"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddStock